import { useState } from "react";
import Sparkline from "../shared/Sparkline";
import ScoreRing from "../shared/ScoreRing";

function cn(...cs) { return cs.filter(Boolean).join(" "); }

function InfoIcon() {
    return (
        <svg width="11" height="11" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" className="text-gray-400 flex-shrink-0">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
        </svg>
    );
}
function ArrowRight({ size = 14 }) {
    return <svg width={size} height={size} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>;
}
function DotsIcon() {
    return <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/></svg>;
}

export default function AccountHome({ clientData, setView, setActiveDomain }) {
    const [banner, setBanner] = useState(true);
    const [auditTab, setAuditTab] = useState("All");

    // Use real data where available, fall back to safe defaults
    const biz = clientData?.biz || "Your";
    const domains = clientData?.domains_detail || [];
    const analytics = clientData?.analytics || {
        security:    { insights: 0, breakdown: "—", encryptedRequests: "—", trend: [] },
        performance: { cacheRate: "—", cacheChange: 0, cacheTrend: [] },
        activity:    { webTrafficFormatted: "—", webTrafficChange: 0, webTrend: [], workersInvocations: 0 },
    };
    const workers     = clientData?.workers     || [];
    const auditLogs   = clientData?.auditLogs   || [];
    const nextSteps   = clientData?.nextSteps   || [];

    const filteredLogs = auditTab === "All" ? auditLogs
        : auditTab === "Dashboard" ? auditLogs.filter(l => !l.domain?.includes("."))
        : auditLogs.filter(l => l.actor?.includes("api"));

    return (
        <main className="flex-1 overflow-y-auto bg-white">
            {banner && (
                <div className="border-b border-blue-100 bg-blue-50 px-6 py-2.5 flex items-center gap-3 text-sm">
                    <span className="text-gray-700">Know exactly what you'll be charged. Get daily visibility into usage-based costs across your HerStorySEO account.</span>
                    <button className="ml-auto px-3 py-1 border border-gray-400 rounded text-xs font-medium hover:bg-white whitespace-nowrap">View Dashboard</button>
                    <button onClick={() => setBanner(false)} className="text-gray-400 hover:text-gray-700">
                        <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                </div>
            )}

            <div className="p-6 max-w-[1100px]">
                {/* Title */}
                <div className="text-xs text-gray-400 mb-1">Account home</div>
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-semibold text-gray-900">{biz}'s Account</h1>
                    <div className="flex items-center gap-2">
                        <button className="w-7 h-7 flex items-center justify-center text-gray-400 hover:bg-gray-100 rounded">
                            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                        </button>
                        <button className="w-7 h-7 flex items-center justify-center text-gray-400 hover:bg-gray-100 rounded"><DotsIcon/></button>
                        <button className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700">
                            <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                            Add
                        </button>
                    </div>
                </div>

                {/* Analytics header */}
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-semibold text-gray-900">Analytics</h2>
                    <button className="flex items-center gap-1.5 text-xs text-gray-500 border border-gray-200 rounded px-2.5 py-1 hover:bg-gray-50">
                        <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                        Last 24 hours
                    </button>
                </div>

                {/* Analytics 3-col */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                    {/* Security */}
                    <div className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center gap-1.5 text-xs font-medium text-gray-600 mb-3">
                            <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                            Security
                        </div>
                        <div className="flex gap-4">
                            <div>
                                <div className="flex items-center gap-1 text-[11px] text-gray-400 mb-1">Security insights <InfoIcon/></div>
                                <div className="text-2xl font-semibold text-gray-900">{analytics.security.insights}</div>
                                <div className="text-[11px] text-gray-400">{analytics.security.breakdown}</div>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1 text-[11px] text-gray-400 mb-1">Encrypted requests <InfoIcon/></div>
                                <div className="text-lg font-semibold text-gray-900">
                                    {analytics.security.encryptedRequests}
                                </div>
                                <Sparkline data={analytics.security.trend} width={90} height={22}/>
                            </div>
                        </div>
                    </div>

                    {/* Performance */}
                    <div className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center gap-1.5 text-xs font-medium text-gray-600 mb-3">
                            <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                            Performance
                        </div>
                        <div className="flex gap-4">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1 text-[11px] text-gray-400 mb-1">Cache rate <InfoIcon/></div>
                                <div className="text-lg font-semibold text-gray-900">
                                    {analytics.performance.cacheRate}
                                    {analytics.performance.cacheChange > 0 && (
                                        <span className="text-[11px] text-green-600 font-normal ml-1">↗{analytics.performance.cacheChange}%</span>
                                    )}
                                </div>
                                <Sparkline data={analytics.performance.cacheTrend} width={70} height={22}/>
                            </div>
                            <div>
                                <div className="text-[11px] text-gray-400 mb-1">CPU time (P90)</div>
                                <div className="text-lg font-semibold text-gray-900">— <span className="text-xs font-normal text-gray-400">ms</span></div>
                            </div>
                        </div>
                    </div>

                    {/* Activity */}
                    <div className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center gap-1.5 text-xs font-medium text-gray-600 mb-3">
                            <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                            Activity
                        </div>
                        <div className="flex gap-4">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1 text-[11px] text-gray-400 mb-1">Web traffic <InfoIcon/></div>
                                <div className="text-lg font-semibold text-gray-900">
                                    {analytics.activity.webTrafficFormatted}
                                    {analytics.activity.webTrafficChange < 0 && (
                                        <span className="text-[11px] text-red-500 font-normal ml-1">↘{Math.abs(analytics.activity.webTrafficChange)}%</span>
                                    )}
                                </div>
                                <Sparkline data={analytics.activity.webTrend} width={70} height={22}/>
                            </div>
                            <div>
                                <div className="text-[11px] text-gray-400 mb-1">Workers invocations</div>
                                <div className="text-lg font-semibold text-gray-900">{analytics.activity.workersInvocations}</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom grid: Domains + Workers + Zero Trust */}
                <div className="grid grid-cols-3 gap-4 mb-4">
                    {/* Domains card */}
                    <div className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <span className="text-xs font-semibold text-gray-800">Domains</span>
                            <span className="text-[10px] bg-gray-100 text-gray-600 rounded px-1.5 py-0.5 font-medium">{domains.length}</span>
                            <div className="ml-auto flex items-center gap-1 text-gray-400">
                                <button className="hover:text-gray-600 p-0.5">
                                    <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                                </button>
                                <button className="hover:text-gray-600 p-0.5"><DotsIcon/></button>
                                <button className="hover:text-gray-600 p-0.5"><ArrowRight size={12}/></button>
                            </div>
                        </div>
                        <div className="space-y-2.5">
                            {domains.map(d => (
                                <div key={d.id} className="flex items-center gap-2 text-xs">
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/></svg>
                                    <button onClick={() => { setActiveDomain(d.name); setView("domain"); }}
                                            className="text-blue-600 hover:underline flex-1 text-left truncate">{d.name}</button>
                                    <Sparkline data={d.trend} width={50} height={18}/>
                                    <span className="text-gray-400">{d.uniqueVisitorsFormatted}</span>
                                    <button className="text-gray-300 hover:text-gray-500"><DotsIcon/></button>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Workers & Pages */}
                    <div className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <span className="text-xs font-semibold text-gray-800">Workers and Pages</span>
                            <span className="text-[10px] bg-gray-100 text-gray-600 rounded px-1.5 py-0.5">{workers.length}</span>
                            <div className="ml-auto flex items-center gap-1 text-gray-400">
                                <button className="hover:text-gray-600 p-0.5"><DotsIcon/></button>
                                <button className="hover:text-gray-600 p-0.5"><ArrowRight size={12}/></button>
                            </div>
                        </div>
                        {workers.map(w => (
                            <div key={w.name} className="flex items-center gap-2 text-xs">
                                <div className="w-5 h-5 bg-gray-800 rounded flex items-center justify-center">
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="white"><path d="M13 2L3 14h9l-1 8 10-12h-9z"/></svg>
                                </div>
                                <span className="text-blue-600 hover:underline cursor-pointer flex-1">{w.name}</span>
                                <span className="text-gray-400">{w.lastDeployed}</span>
                                <button className="text-gray-300 hover:text-gray-500"><DotsIcon/></button>
                            </div>
                        ))}
                    </div>

                    {/* Zero Trust */}
                    <div className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-xs font-semibold text-gray-800">Zero Trust security</span>
                            <button className="text-gray-400 hover:text-gray-600"><ArrowRight size={12}/></button>
                        </div>
                        <div className="flex flex-col items-center text-center py-1">
                            <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center mb-2">
                                <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="#3b82f6" strokeWidth="1.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></svg>
                            </div>
                            <div className="font-semibold text-gray-900 text-xs mb-1">Secure access for your team and tools</div>
                            <div className="text-[11px] text-gray-400 mb-2">Control who reaches your internal apps and data, without a VPN</div>
                            <button className="px-3 py-1 border border-gray-200 rounded text-xs text-gray-600 hover:bg-gray-50">Get started</button>
                        </div>
                    </div>
                </div>

                {/* Audit logs + Next steps */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-xs font-semibold text-gray-800">Audit logs</span>
                            <button className="text-gray-400 hover:text-gray-600"><ArrowRight size={12}/></button>
                        </div>
                        <div className="flex gap-4 border-b border-gray-100 mb-3">
                            {["All", "Dashboard", "API"].map(t => (
                                <button key={t} onClick={() => setAuditTab(t)}
                                        className={`pb-2 text-xs border-b-2 transition-colors ${auditTab === t ? "border-blue-600 text-blue-600 font-medium" : "border-transparent text-gray-400 hover:text-gray-600"}`}>
                                    {t}
                                </button>
                            ))}
                        </div>
                        <div className="space-y-2">
                            {filteredLogs.slice(0, 3).map((log, i) => (
                                <div key={i} className="flex items-center gap-2 text-xs">
                                    <span className="text-gray-800 font-medium flex-1">{log.action}</span>
                                    <span className="text-gray-400 truncate max-w-[110px]">{log.domain}</span>
                                    <span className="text-gray-400 whitespace-nowrap">{log.time}</span>
                                </div>
                            ))}
                            {filteredLogs.length === 0 && (
                                <p className="text-xs text-gray-400">No logs yet.</p>
                            )}
                        </div>
                    </div>

                    <div className="border border-gray-200 rounded-lg p-4">
                        <div className="text-xs font-semibold text-gray-800 mb-3">Next steps</div>
                        <div className="space-y-1">
                            {nextSteps.map((step, i) => (
                                <button key={i} className="w-full flex items-center gap-2 text-xs py-1 group">
                                    <div className={cn("w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0 border",
                                        step.done ? "bg-green-500 border-green-500" : "border-gray-300 group-hover:border-blue-400")}>
                                        {step.done && <svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12" strokeLinecap="round"/></svg>}
                                    </div>
                                    <span className={cn("flex-1 text-left", step.done ? "text-gray-400 line-through" : "text-blue-600 group-hover:underline")}>{step.label}</span>
                                    {!step.done && <ArrowRight size={11}/>}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* SEO Overview — per-domain cards */}
                {domains.length > 0 && (
                    <div>
                        <h2 className="text-sm font-semibold text-gray-900 mb-3">SEO Overview</h2>
                        <div className="grid grid-cols-2 gap-4">
                            {domains.map(d => (
                                <div key={d.id} className="border border-gray-200 rounded-lg p-4">
                                    <div className="flex items-center gap-2 mb-4">
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/></svg>
                                        <button onClick={() => { setActiveDomain(d.name); setView("domain"); }}
                                                className="text-blue-600 hover:underline font-medium text-xs">{d.name}</button>
                                        <span className={cn("ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full",
                                            d.plan === "Growth" ? "bg-blue-50 text-blue-600" : "bg-gray-100 text-gray-500")}>{d.plan}</span>
                                        <ScoreRing score={d.overallSeoScore}/>
                                    </div>

                                    <div className="grid grid-cols-2 gap-2 mb-3">
                                        {[
                                            { label: "Organic Traffic", ...d.seo.organicTraffic },
                                            { label: "Keywords",        ...d.seo.keywordsRanking },
                                            { label: "Backlinks",       ...d.seo.backlinks },
                                            { label: "Page Speed",      ...d.seo.pageSpeed },
                                        ].map(m => (
                                            <div key={m.label} className="bg-gray-50 rounded-lg p-2.5">
                                                <div className="text-[10px] text-gray-400 mb-1">{m.label}</div>
                                                <div className="flex items-end justify-between">
                                                    <span className="text-base font-semibold text-gray-900">{m.value}</span>
                                                    <Sparkline data={m.trend} width={44} height={20}/>
                                                </div>
                                                <div className={cn("text-[10px] font-semibold mt-0.5", m.change >= 0 ? "text-green-600" : "text-red-500")}>
                                                    {m.change >= 0 ? "▲" : "▼"} {Math.abs(m.change)}%
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="grid grid-cols-4 gap-1.5 mb-3">
                                        {[
                                            { label: "Domain Auth.", val: d.seo.domainAuthority },
                                            { label: "Indexed",      val: d.seo.indexedPages },
                                            { label: "Crawl Errors", val: d.seo.crawlErrors, alert: d.seo.crawlErrors > 0 },
                                            { label: "Core Vitals",  val: d.seo.coreWebVitals === "Good" ? "✓ Good" : "⚠ Fair" },
                                        ].map(s => (
                                            <div key={s.label} className="text-center bg-white border border-gray-100 rounded p-1.5">
                                                <div className="text-[9px] text-gray-400 mb-0.5">{s.label}</div>
                                                <div className={cn("text-xs font-bold", s.alert ? "text-red-500" : "text-gray-800")}>{s.val}</div>
                                            </div>
                                        ))}
                                    </div>

                                    {d.seo.issues.length > 0 && (
                                        <div className="space-y-1">
                                            {d.seo.issues.map((iss, i) => (
                                                <div key={i} className="flex items-center gap-2 text-[11px]">
                                                    <span className={cn("px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide",
                                                        iss.type === "warning" ? "bg-amber-100 text-amber-700" : "bg-sky-100 text-sky-700")}>
                                                        {iss.type}
                                                    </span>
                                                    <span className="text-gray-500">{iss.message}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </main>
    );
}
